import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";

interface ConsentConfig {
  initialSmsText: string;
  widgetDisclosureText: string;
  policyVersion: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface ConsentRecord {
  id: number;
  phoneNumber: string;
  velaroPhoneNumber: string;
  consentMethod: string;
  consentTextShown: string;
  ipAddress: string | null;
  userAgent: string | null;
  contactName: string | null;
  consentedAt: string;
}

interface RecordsPage {
  total: number;
  page: number;
  pageSize: number;
  records: ConsentRecord[];
}

function MethodBadge({ method }: { method: string }) {
  if (method === "widget") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        Widget
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
      Inbound SMS
    </span>
  );
}

export default function SmsConsentPanel() {
  const api = useApi();

  // Config state
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [config, setConfig] = useState<ConsentConfig>({
    initialSmsText: "",
    widgetDisclosureText: "",
    policyVersion: "1.0",
    updatedAt: null,
    updatedBy: null
  });
  const [configMessage, setConfigMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [configOpen, setConfigOpen] = useState(false);

  // Records state
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsData, setRecordsData] = useState<RecordsPage | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expandedRecord, setExpandedRecord] = useState<number | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await api.messaging.get("Sms/Consent/Config");
      const data = await resp.json();
      setConfig({
        initialSmsText: data.initialSmsText ?? "",
        widgetDisclosureText: data.widgetDisclosureText ?? "",
        policyVersion: data.policyVersion ?? "1.0",
        updatedAt: data.updatedAt ?? null,
        updatedBy: data.updatedBy ?? null
      });
    } catch {
      // keep defaults
    } finally {
      setConfigLoading(false);
    }
  }, [api.messaging]);

  const fetchRecords = useCallback(async (p = 1, q = "") => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), pageSize: "50" });
      if (q) params.set("search", q);
      const resp = await api.messaging.get(`Sms/Consent/Records?${params}`);
      const data = await resp.json();
      setRecordsData(data);
    } catch {
      setRecordsData(null);
    } finally {
      setRecordsLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (recordsOpen) fetchRecords(page, search);
  }, [recordsOpen, page, search, fetchRecords]);

  async function saveConfig() {
    setConfigSaving(true);
    setConfigMessage(null);
    try {
      const resp = await api.messaging.put("Sms/Consent/Config", {
        initialSmsText: config.initialSmsText,
        widgetDisclosureText: config.widgetDisclosureText
      });
      if (resp.ok) {
        setConfigMessage({ text: "Opt-in settings saved.", type: "success" });
        fetchConfig();
      } else {
        const data = await resp.json().catch(() => ({}));
        setConfigMessage({ text: data.error ?? "Failed to save. Please try again.", type: "error" });
      }
    } catch {
      setConfigMessage({ text: "Failed to save. Please try again.", type: "error" });
    } finally {
      setConfigSaving(false);
    }
  }

  const totalPages = recordsData ? Math.ceil(recordsData.total / recordsData.pageSize) : 0;

  return (
    <div className="space-y-4 border-t border-slate-200 pt-6 mt-6">
      {/* ── Opt-in Settings ── */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setConfigOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
          aria-expanded={configOpen}
        >
          <div className="flex items-center gap-2">
            <i className="fas fa-file-signature text-slate-500" aria-hidden="true" />
            <span className="font-medium text-slate-800 text-sm">SMS Opt-in Settings</span>
            <span className="text-xs text-slate-400">
              — customize the consent text sent to new contacts
            </span>
          </div>
          <i
            className={`fas fa-chevron-${configOpen ? "up" : "down"} text-slate-400 text-xs`}
            aria-hidden="true"
          />
        </button>

        {configOpen && (
          <div className="p-4 space-y-5">
            {configLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <span className="font-semibold">TCPA requirement:</span> Your opt-in text{" "}
                  <strong>must</strong> include opt-out instructions ("Reply STOP to opt out" or
                  equivalent). Velaro validates this before saving. Customers reaching out via SMS
                  or the web widget give implied consent — you may not initiate contact without
                  prior express written consent.
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Initial SMS text
                    <span className="ml-2 text-xs text-slate-400 font-normal">
                      sent automatically as the first message when a new contact texts in or submits the widget
                    </span>
                  </label>
                  <textarea
                    rows={3}
                    className="input w-full text-sm font-mono"
                    value={config.initialSmsText}
                    onChange={(e) => setConfig((c) => ({ ...c, initialSmsText: e.target.value }))}
                    placeholder="e.g. An agent will be with you shortly. Reply STOP to opt out."
                  />
                  <div className="text-xs text-slate-400 mt-1">
                    {config.initialSmsText.length} chars · Twilio max: 160 per segment
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Widget disclosure text
                    <span className="ml-2 text-xs text-slate-400 font-normal">
                      shown above the phone number field in the web widget before the customer submits
                    </span>
                  </label>
                  <textarea
                    rows={2}
                    className="input w-full text-sm"
                    value={config.widgetDisclosureText}
                    onChange={(e) => setConfig((c) => ({ ...c, widgetDisclosureText: e.target.value }))}
                    placeholder="e.g. By providing your phone number you agree to receive SMS. Reply STOP to opt out."
                  />
                </div>

                {config.updatedAt && (
                  <p className="text-xs text-slate-400">
                    Last updated {config.updatedAt} by {config.updatedBy || "system"}
                  </p>
                )}

                {configMessage && (
                  <div
                    className={`px-3 py-2 rounded text-sm ${
                      configMessage.type === "success"
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                  >
                    {configMessage.text}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveConfig}
                    disabled={configSaving}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {configSaving ? "Saving..." : "Save Opt-in Settings"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Consent Records ── */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setRecordsOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
          aria-expanded={recordsOpen}
        >
          <div className="flex items-center gap-2">
            <i className="fas fa-shield-halved text-slate-500" aria-hidden="true" />
            <span className="font-medium text-slate-800 text-sm">Consent Audit Log</span>
            <span className="text-xs text-slate-400">
              — permanent TCPA compliance records
            </span>
          </div>
          <i
            className={`fas fa-chevron-${recordsOpen ? "up" : "down"} text-slate-400 text-xs`}
            aria-hidden="true"
          />
        </button>

        {recordsOpen && (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" aria-hidden="true" />
                <input
                  className="input pl-8 text-sm w-full"
                  placeholder="Search by phone or name..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              {recordsData && (
                <span className="text-xs text-slate-500">
                  {recordsData.total.toLocaleString()} record{recordsData.total !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
              <i className="fas fa-lock text-blue-400 mr-1" aria-hidden="true" />
              These records are <strong>permanent and immutable</strong> — they cannot be edited or
              deleted. They capture the exact consent text shown/sent, the timestamp, and browser
              fingerprint (IP + User-Agent) for legally defensible TCPA compliance records.
            </div>

            {recordsLoading ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner />
              </div>
            ) : recordsData && recordsData.records.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm bg-white border border-slate-200 rounded-lg">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 text-xs">Phone</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 text-xs">Via</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 text-xs">Contact</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 text-xs">IP Address</th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 text-xs">Consented At (UTC)</th>
                        <th className="px-3 py-2 text-xs" style={{ width: 1 }}></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recordsData.records.map((r) => (
                        <React.Fragment key={r.id}>
                          <tr className="hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono text-xs">{r.phoneNumber}</td>
                            <td className="px-3 py-2">
                              <MethodBadge method={r.consentMethod} />
                            </td>
                            <td className="px-3 py-2 text-slate-600 text-xs">{r.contactName || "—"}</td>
                            <td className="px-3 py-2 font-mono text-xs text-slate-500">
                              {r.ipAddress || "—"}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-600">{r.consentedAt}</td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                onClick={() => setExpandedRecord(expandedRecord === r.id ? null : r.id)}
                                aria-label="View consent text"
                              >
                                {expandedRecord === r.id ? "Hide" : "Details"}
                              </button>
                            </td>
                          </tr>
                          {expandedRecord === r.id && (
                            <tr>
                              <td colSpan={6} className="px-3 pb-3">
                                <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
                                  <div>
                                    <span className="text-xs font-semibold text-slate-600">Velaro number: </span>
                                    <span className="text-xs font-mono text-slate-700">{r.velaroPhoneNumber}</span>
                                  </div>
                                  <div>
                                    <span className="text-xs font-semibold text-slate-600">Exact consent text shown/sent:</span>
                                    <div className="mt-1 bg-white border border-slate-200 rounded p-2 text-xs text-slate-700 italic">
                                      "{r.consentTextShown}"
                                    </div>
                                  </div>
                                  {r.userAgent && (
                                    <div>
                                      <span className="text-xs font-semibold text-slate-600">User-Agent: </span>
                                      <span className="text-xs text-slate-500 break-all">{r.userAgent}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <span className="text-xs text-slate-500">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-400 text-sm">
                {search ? "No consent records match your search." : "No consent records yet. Records appear here when contacts first opt in to SMS."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
